import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import type { Database } from "@/types/database.types";
import { CalendarViewModel, KanbanViewModel, TaskDialog, TaskSearchDialog } from "@/components/project";
import { useProjects } from "@/contexts/ProjectsContext";
import { useAuth } from "@/contexts/AuthContext";
import { useTaskDialog } from "@/contexts/TaskDialogContext";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Search,
  ChevronDown,
  ChevronRight,
  X,
  Clock,
  List,
  LayoutGrid,
  Calendar as CalendarIcon,
} from "lucide-react";
import { format, isToday, isTomorrow, startOfDay, addDays } from "date-fns";

type TaskWithDetailsRow = Database["public"]["Views"]["tasks_with_details"]["Row"];
type TaskRow = Database["public"]["Tables"]["tasks"]["Row"];
type TaskPartialUpdate = Partial<TaskRow> & { id: string };

type ViewMode = "list" | "kanban" | "calendar";

interface TaskSection {
  id: string;
  title: string;
  tasks: TaskWithDetailsRow[];
  color: string;
  count: number;
}

export function Tasks() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { homepageView, setHomepageView } = useUserPreferences();
  const isMobile = useIsMobile();
  const { user, loading: authLoading } = useAuth();
  const { projects, loading: projectsLoading } = useProjects();
  const { taskToOpen, clearTaskToOpen } = useTaskDialog();

  const [viewMode, setViewMode] = useState<ViewMode>(
    (searchParams.get("view") as ViewMode) || (homepageView as ViewMode) || "list"
  );

  useEffect(() => {
    const urlView = searchParams.get("view") as ViewMode;
    if (urlView && urlView !== viewMode) {
      setViewMode(urlView);
    }
  }, [searchParams, viewMode]);

  const [tasks, setTasks] = useState<TaskWithDetailsRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<TaskWithDetailsRow | null>(null);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Unified filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState<string>("all");
  const [selectedAssigneeId, setSelectedAssigneeId] = useState<string>("all");
  const [hideCompleted, setHideCompleted] = useState<boolean>(false);

  const [projectMembers, setProjectMembers] = useState<
    Array<{
      user_id: string;
      username: string | null;
      full_name: string | null;
      avatar_url: string | null;
    }>
  >([]);
  const hasFetchedRef = useRef(false);
  const lastProjectsRef = useRef<string>("");
  const previousProjectIdRef = useRef<string>("all");

  // List view state
  const [overdueExpanded, setOverdueExpanded] = useState(true);
  const [backlogExpanded, setBacklogExpanded] = useState(true);
  const [upcomingExpanded, setUpcomingExpanded] = useState(true);
  const [doneExpanded, setDoneExpanded] = useState(false);
  const [quickAddValue, setQuickAddValue] = useState("");
  const [quickAddLoading, setQuickAddLoading] = useState(false);
  const [quickAddProjectId, setQuickAddProjectId] = useState<string>("");

  useEffect(() => {
    if (projects.length > 0 && !quickAddProjectId) {
      setQuickAddProjectId(projects[0].project_id);
    }
  }, [projects, quickAddProjectId]);

  useEffect(() => {
    if (taskToOpen) {
      const taskExists = tasks.some((t) => t.id === taskToOpen.id);
      if (taskExists) {
        setEditingTask(taskToOpen);
        setTaskDialogOpen(true);
        clearTaskToOpen();
      } else {
        if (taskToOpen.project_id) {
          navigate(`/project/${taskToOpen.project_id}`);
        }
        clearTaskToOpen();
      }
    }
  }, [taskToOpen, tasks, clearTaskToOpen, navigate]);

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    setSearchParams({ view: mode });
    if (mode !== "list") {
      setHomepageView(mode);
    }
  };

  const fetchTasks = useCallback(async () => {
    if (authLoading || projectsLoading) return;

    setLoading(true);
    setError(null);

    try {
      if (!user) {
        navigate("/login", { replace: true });
        setLoading(false);
        return;
      }

      const projectIds = projects?.map((p: { project_id: string }) => p.project_id) || [];

      if (projectIds.length === 0) {
        setTasks([]);
        setLoading(false);
        return;
      }

      const { data: tasksData, error: tasksError } = await supabase
        .from("tasks_with_details")
        .select("*")
        .in("project_id", projectIds)
        .order("due_date", { ascending: true, nullsFirst: false })
        .order("updated_at", { ascending: false });

      if (tasksError) {
        throw tasksError;
      }

      setTasks(tasksData || []);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load tasks";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [navigate, projects, user, authLoading, projectsLoading]);

  useEffect(() => {
    if (authLoading || projectsLoading) return;
    if (!projects) return;
    if (!user) return;

    const projectIdsStr = projects.map((p: { project_id: string }) => p.project_id).join(",");
    if (projectIdsStr !== lastProjectsRef.current || !hasFetchedRef.current) {
      lastProjectsRef.current = projectIdsStr;
      hasFetchedRef.current = true;
      fetchTasks();
    }
  }, [projects, user, authLoading, projectsLoading, fetchTasks]);

  const uniqueProjects = Array.from(
    new Map(
      tasks
        .filter((t) => t.project_id && t.project_name)
        .map((t) => [t.project_id, { id: t.project_id!, name: t.project_name! }]),
    ).values(),
  );

  useEffect(() => {
    const fetchMembers = async () => {
      if (selectedProjectId === "all") {
        setProjectMembers([]);
        if (
          previousProjectIdRef.current !== "all" &&
          selectedAssigneeId !== "all" &&
          selectedAssigneeId !== "unassigned"
        ) {
          setSelectedAssigneeId("all");
        }
        previousProjectIdRef.current = "all";
        return;
      }

      try {
        const { data: membersData, error } = await supabase
          .from("project_members_with_profiles")
          .select("*")
          .eq("project_id", selectedProjectId)
          .order("role", { ascending: false })
          .order("added_at", { ascending: true });

        if (error) {
          console.error("Error fetching project members:", error);
          setProjectMembers([]);
        } else {
          setProjectMembers(
            (membersData || []).map((m) => ({
              user_id: m.user_id!,
              username: m.username,
              full_name: m.full_name,
              avatar_url: m.avatar_url,
            })),
          );
          if (
            selectedAssigneeId !== "all" &&
            selectedAssigneeId !== "unassigned" &&
            !membersData?.some((m) => m.user_id === selectedAssigneeId)
          ) {
            setSelectedAssigneeId("all");
          }
        }
      } catch (err) {
        console.error("Error fetching project members:", err);
        setProjectMembers([]);
      }
    };

    fetchMembers();
  }, [selectedProjectId]);

  const assigneeOptions = [
    { id: "all", label: "All Assignees" },
    { id: "unassigned", label: "Unassigned" },
    ...projectMembers.map((m) => ({
      id: m.user_id,
      label: m.full_name || m.username || "Unknown",
    })),
  ];

  const isTaskStale = (task: TaskWithDetailsRow): boolean => {
    if (task.status !== "done" || !task.completed_at) return false;
    const completedDate = new Date(task.completed_at);
    const staleThreshold = 14 * 24 * 60 * 60 * 1000;
    return Date.now() - completedDate.getTime() > staleThreshold;
  };

  // Base filter: project, assignee, hideCompleted, search
  const baseFilteredTasks = tasks.filter((task) => {
    if (selectedProjectId !== "all" && task.project_id !== selectedProjectId) return false;
    if (selectedAssigneeId !== "all" && task.assigned_to !== selectedAssigneeId) {
      if (selectedAssigneeId === "unassigned" && task.assigned_to !== null) return false;
      if (selectedAssigneeId !== "unassigned" && task.assigned_to !== selectedAssigneeId) return false;
    }
    if (task.status === "done" && isTaskStale(task)) return false;
    if (hideCompleted && task.status === "done") return false;

    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const titleMatch = task.title?.toLowerCase().includes(q);
      const projectMatch = task.project_name?.toLowerCase().includes(q);
      if (!titleMatch && !projectMatch) return false;
    }

    return true;
  });

  const todoTasks = baseFilteredTasks.filter((t) => t.status === "todo");
  const inProgressTasks = baseFilteredTasks.filter((t) => t.status === "in_progress");
  const reviewTasks = baseFilteredTasks.filter((t) => t.status === "review");
  const doneTasks = baseFilteredTasks.filter((t) => t.status === "done");

  // List view sections based on due date
  const listSections = ((): TaskSection[] => {
    const now = new Date();
    const today = startOfDay(now);
    const endOfWeek = addDays(today, 7);

    const overdue: TaskWithDetailsRow[] = [];
    const backlog: TaskWithDetailsRow[] = [];
    const upcoming: TaskWithDetailsRow[] = [];
    const done: TaskWithDetailsRow[] = [];

    baseFilteredTasks.forEach((task) => {
      if (task.status === "done") {
        done.push(task);
        return;
      }

      if (!task.due_date) {
        backlog.push(task);
        return;
      }

      const due = startOfDay(new Date(task.due_date));

      if (due < today) {
        overdue.push(task);
      } else if (due <= endOfWeek) {
        upcoming.push(task);
      } else {
        backlog.push(task);
      }
    });

    return [
      { id: "overdue", title: "Overdue", tasks: overdue, color: "text-destructive", count: overdue.length },
      { id: "backlog", title: "Backlog", tasks: backlog, color: "text-muted-foreground", count: backlog.length },
      { id: "upcoming", title: "Upcoming", tasks: upcoming, color: "text-primary", count: upcoming.length },
      { id: "done", title: "Done", tasks: done, color: "text-green-500", count: done.length },
    ];
  })();

  const totalCount = listSections.reduce((sum, s) => sum + s.count, 0);

  const handleToggleComplete = async (task: TaskWithDetailsRow) => {
    if (!user || !task.id) return;

    const newStatus = task.status === "done" ? "todo" : "done";

    const { error: updateError } = await supabase
      .from("tasks")
      .update({
        status: newStatus,
        completed_at: newStatus === "done" ? new Date().toISOString() : null,
        completed_by: newStatus === "done" ? user.id : null,
        updated_at: new Date().toISOString(),
      })
      .eq("id", task.id);

    if (!updateError) {
      setTasks((prev) =>
        prev.map((t) =>
          t.id === task.id
            ? { ...t, status: newStatus as TaskWithDetailsRow["status"], completed_at: newStatus === "done" ? new Date().toISOString() : null }
            : t,
        ),
      );
    }
  };

  const handleEditTask = (task: TaskWithDetailsRow) => {
    setEditingTask(task);
    setTaskDialogOpen(true);
  };

  const handleViewTask = (task: TaskWithDetailsRow) => {
    handleEditTask(task);
  };

  const handleMarkComplete = async (task: TaskWithDetailsRow) => {
    const newStatus: "todo" | "done" = task.status === "done" ? "todo" : "done";
    setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, status: newStatus } : t)));

    const { error } = await supabase
      .from("tasks")
      .update({ status: newStatus, updated_at: new Date().toISOString() })
      .eq("id", task.id!);

    if (error) {
      setTasks((prev) => prev.map((t) => (t.id === task.id ? task : t)));
      console.error("Error updating task:", error);
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!confirm("Are you sure you want to delete this task?")) return;

    const taskToDelete = tasks.find((t) => t.id === taskId);
    setTasks((prev) => prev.filter((t) => t.id !== taskId));

    const { error } = await supabase.from("tasks").delete().eq("id", taskId);

    if (error) {
      if (taskToDelete) {
        setTasks((prev) => [...prev, taskToDelete]);
      }
      console.error("Error deleting task:", error);
    }
  };

  const handleMoveTask = async (taskId: string, toStatus: "todo" | "in_progress" | "review" | "done") => {
    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;

    const updatedTask = { ...task, status: toStatus, updated_at: new Date().toISOString() };
    setTasks((prev) => prev.map((t) => (t.id === taskId ? updatedTask : t)));

    const { error } = await supabase
      .from("tasks")
      .update({ status: toStatus, updated_at: new Date().toISOString() })
      .eq("id", taskId);

    if (error) {
      setTasks((prev) => prev.map((t) => (t.id === taskId ? task : t)));
      console.error("Error moving task:", error);
    }
  };

  const handleTaskDateChange = useCallback(
    async (task: TaskWithDetailsRow, newDate: Date) => {
      const newDateStr = newDate.toISOString().split("T")[0];
      const oldDateStr = task.due_date ? new Date(task.due_date).toISOString().split("T")[0] : null;

      if (oldDateStr === newDateStr) return;

      const updatedTask: TaskWithDetailsRow = { ...task, due_date: newDateStr, updated_at: new Date().toISOString() };
      setTasks((prev) => prev.map((t) => (t.id === task.id ? updatedTask : t)));

      const { error } = await supabase
        .from("tasks")
        .update({ due_date: newDateStr, updated_at: new Date().toISOString() })
        .eq("id", task.id!);

      if (error) {
        setTasks((prev) => prev.map((t) => (t.id === task.id ? task : t)));
        console.error("Error updating task date:", error);
      }
    },
    [],
  );

  const handleTaskSuccess = useCallback(
    (result?: TaskWithDetailsRow | TaskPartialUpdate) => {
      setTaskDialogOpen(false);
      setEditingTask(null);
      if (result) {
        if ("id" in result && result.id) {
          if (result.status) {
            setTasks((prev) => prev.map((t) => (t.id === result.id ? { ...t, ...result } : t)));
          } else {
            fetchTasks();
          }
        } else if (result && "title" in result) {
          fetchTasks();
        }
      } else {
        fetchTasks();
      }
    },
    [fetchTasks],
  );

  const handleQuickAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const title = quickAddValue.trim();
    if (!title || projects.length === 0 || !user) return;

    setQuickAddLoading(true);
    try {
      const targetProjectId = quickAddProjectId || projects[0]?.project_id;
      if (!targetProjectId) return;
      await supabase.from("tasks").insert({
        title,
        status: "todo",
        project_id: targetProjectId,
        created_by: user.id,
        due_date: null,
      }).select().single();

      setQuickAddValue("");
      fetchTasks();
    } catch (err) {
      console.error("Quick add failed:", err);
    } finally {
      setQuickAddLoading(false);
    }
  };

  const formatDueDate = (dueDate: string | null) => {
    if (!dueDate) return null;
    const date = new Date(dueDate);
    if (isToday(date)) return "Today";
    if (isTomorrow(date)) return "Tomorrow";
    return format(date, "MMM d");
  };

  const getPriorityColor = (priority: string | null) => {
    switch (priority) {
      case "high": return "bg-red-500/20 text-red-600 dark:text-red-400";
      case "medium": return "bg-yellow-500/20 text-yellow-600 dark:text-yellow-400";
      case "low": return "bg-blue-500/20 text-blue-600 dark:text-blue-400";
      default: return "bg-muted text-muted-foreground";
    }
  };

  const hasActiveFilters = searchQuery || selectedProjectId !== "all" || selectedAssigneeId !== "all" || hideCompleted;

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedProjectId("all");
    setSelectedAssigneeId("all");
    setHideCompleted(false);
  };

  if (loading && tasks.length === 0 && !hasFetchedRef.current) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (error && !tasks.length) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-destructive">{error}</div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden min-h-0">
      {/* Header */}
      <div className="border-b bg-background px-4 md:px-6 py-4 shrink-0">
          {/* Title Row */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold tracking-tight">My Tasks</h1>
              {totalCount > 0 && (
                <span className="inline-flex items-center justify-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                  {totalCount}
                </span>
              )}
              {loading && (
                <div className="size-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              )}
            </div>
          </div>

          {/* Mobile Controls Row - below title */}
          <div className="flex md:hidden items-center justify-between gap-2 mb-4">
            {/* View Toggle */}
            <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
              <Button
                variant={viewMode === "list" ? "default" : "ghost"}
                size="sm"
                className="h-7 px-2"
                onClick={() => handleViewModeChange("list")}
              >
                <List className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === "kanban" ? "default" : "ghost"}
                size="sm"
                className="h-7 px-2"
                onClick={() => handleViewModeChange("kanban")}
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === "calendar" ? "default" : "ghost"}
                size="sm"
                className="h-7 px-2"
                onClick={() => handleViewModeChange("calendar")}
              >
                <CalendarIcon className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className="gap-1.5"
              >
                <span>Filters</span>
                {hasActiveFilters && (
                  <span className="inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground px-1.5 py-0.5 text-xs font-medium">
                    !
                  </span>
                )}
              </Button>
            </div>
          </div>

          {/* Desktop Controls Row - hidden on mobile */}
          <div className="hidden md:flex items-center justify-between mb-4">
            <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
              <Button
                variant={viewMode === "list" ? "default" : "ghost"}
                size="sm"
                className="h-7 px-2"
                onClick={() => handleViewModeChange("list")}
              >
                <List className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === "kanban" ? "default" : "ghost"}
                size="sm"
                className="h-7 px-2"
                onClick={() => handleViewModeChange("kanban")}
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === "calendar" ? "default" : "ghost"}
                size="sm"
                className="h-7 px-2"
                onClick={() => handleViewModeChange("calendar")}
              >
                <CalendarIcon className="h-4 w-4" />
              </Button>
            </div>
            <Button
              onClick={() => {
                setEditingTask(null);
                setTaskDialogOpen(true);
              }}
              size="sm"
              className="gap-1.5"
            >
              <Plus className="h-4 w-4" />
              Add Task
            </Button>
          </div>

          {/* Unified Filter Bar */}
          <div className={`flex flex-wrap items-center gap-2 ${isMobile ? (showFilters ? "block" : "hidden") : "flex"}`}>
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                placeholder="Search tasks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-accent rounded"
                >
                  <X className="h-3 w-3 text-muted-foreground" />
                </button>
              )}
            </div>

            <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
              <SelectTrigger className="h-9 w-[150px]">
                <SelectValue placeholder="All Projects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                {uniqueProjects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedAssigneeId} onValueChange={setSelectedAssigneeId}>
              <SelectTrigger className="h-9 w-[150px]">
                <SelectValue placeholder="All Assignees" />
              </SelectTrigger>
              <SelectContent>
                {assigneeOptions.map((option) => (
                  <SelectItem key={option.id} value={option.id}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={hideCompleted}
                onChange={(e) => setHideCompleted(e.target.checked)}
                className="rounded border-gray-300"
              />
              <span className="text-sm text-muted-foreground whitespace-nowrap">Hide completed</span>
            </label>

            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="h-9 gap-1.5 text-muted-foreground">
                <X className="h-3 w-3" />
                Clear
              </Button>
            )}
          </div>
        </div>

        {/* Content based on view mode */}
        <div className={`flex-1 overflow-y-auto ${isMobile ? "pb-content-safe" : ""}`}>
          {/* List View */}
          {viewMode === "list" && (
            <div className="p-4 md:p-6">
              <div className="space-y-6">
                {listSections.map((section) => {
                  if (section.tasks.length === 0 && section.id !== "done") return null;

                  const isExpanded =
                    section.id === "overdue" ? overdueExpanded
                    : section.id === "backlog" ? backlogExpanded
                    : section.id === "upcoming" ? upcomingExpanded
                    : doneExpanded;

                  const setExpanded =
                    section.id === "overdue" ? setOverdueExpanded
                    : section.id === "backlog" ? setBacklogExpanded
                    : section.id === "upcoming" ? setUpcomingExpanded
                    : setDoneExpanded;

                  return (
                    <div key={section.id} className="space-y-2">
                      <button
                        onClick={() => setExpanded(!isExpanded)}
                        className="flex items-center gap-2 w-full text-left group"
                      >
                        {isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" /> : <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />}
                        <span className={`text-sm font-semibold ${section.color}`}>{section.title}</span>
                        <span className={`inline-flex items-center justify-center rounded-full px-2 py-0.5 text-xs font-medium ${
                          section.id === "overdue" ? "bg-red-500/20 text-red-600 dark:text-red-400"
                          : section.id === "done" ? "bg-green-500/20 text-green-600 dark:text-green-400"
                          : "bg-muted text-muted-foreground"
                        }`}>
                          {section.count}
                        </span>
                      </button>

                      {isExpanded && (
                        <div className="space-y-1.5">
                          {section.tasks.map((task) => (
                            <div
                              key={task.id}
                              className="group flex items-start gap-3 rounded-lg border bg-card p-3 hover:bg-accent/50 transition-colors"
                            >
                              <Checkbox
                                checked={task.status === "done"}
                                onCheckedChange={() => handleToggleComplete(task)}
                                className="mt-0.5 shrink-0"
                              />
                              <button
                                onClick={() => handleEditTask(task)}
                                className="flex-1 text-left min-w-0"
                              >
                                <p className={`text-sm font-medium leading-tight ${task.status === "done" ? "line-through text-muted-foreground" : ""}`}>
                                  {task.title}
                                </p>
                                <p className="text-xs text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
                                  {task.project_name && (
                                    <Link to={`/project/${task.project_id}`} className="hover:underline" onClick={(e) => e.stopPropagation()}>
                                      {task.project_name}
                                    </Link>
                                  )}
                                  {task.due_date && (
                                    <span className="flex items-center gap-1">
                                      <Clock className="h-3 w-3" />
                                      {formatDueDate(task.due_date)}
                                    </span>
                                  )}
                                </p>
                              </button>
                              <div className="flex items-center gap-1.5 shrink-0">
                                {task.priority && (
                                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getPriorityColor(task.priority)}`}>
                                    {task.priority}
                                  </span>
                                )}
                                {task.tags?.slice(0, 2).map((tag) => (
                                  <span key={tag} className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}

                {totalCount === 0 && (
                  <div className="text-center py-12 text-sm text-muted-foreground">
                    {hasActiveFilters ? "No tasks match your filters." : "No tasks yet. Create one!"}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Kanban View */}
          {viewMode === "kanban" && (
            <div className="p-4">
              <KanbanViewModel
                todoTasks={todoTasks}
                inProgressTasks={inProgressTasks}
                reviewTasks={reviewTasks}
                doneTasks={doneTasks}
                onEdit={handleEditTask}
                onView={handleViewTask}
                onMarkComplete={handleMarkComplete}
                onDelete={handleDeleteTask}
                onMove={handleMoveTask}
              />
            </div>
          )}

          {/* Calendar View */}
          {viewMode === "calendar" && (
            <div className="p-4">
              <CalendarViewModel
                tasks={baseFilteredTasks}
                projects={projects}
                onView={(task) => {
                  setEditingTask(task);
                  setTaskDialogOpen(true);
                }}
                onTaskDateChange={handleTaskDateChange}
                isTaskDialogOpen={taskDialogOpen}
              />
            </div>
          )}
        </div>

        {/* Mobile FAB - Add Task */}
        {isMobile && (
          <Button
            onClick={() => {
              setEditingTask(null);
              setTaskDialogOpen(true);
            }}
            className="fixed bottom-safe-tab right-6 h-14 w-14 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110 z-40"
            size="icon"
            aria-label="Add new task"
          >
            <Plus className="h-6 w-6" />
          </Button>
        )}

        {/* Quick Add - desktop only */}
        <div className="hidden md:block border-t bg-background p-4 shrink-0">
          <form onSubmit={handleQuickAdd} className="flex gap-2">
            <Select value={quickAddProjectId} onValueChange={setQuickAddProjectId}>
              <SelectTrigger className="w-[140px] h-10 shrink-0">
                <SelectValue placeholder="Select project" />
              </SelectTrigger>
              <SelectContent>
                {projects.map((p) => (
                  <SelectItem key={p.project_id} value={p.project_id}>
                    {p.project_title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              placeholder="Add a task to Backlog..."
              value={quickAddValue}
              onChange={(e) => setQuickAddValue(e.target.value)}
              disabled={quickAddLoading || projects.length === 0}
              className="h-10"
            />
            <Button
              type="submit"
              size="sm"
              disabled={!quickAddValue.trim() || quickAddLoading || projects.length === 0}
              className="h-10 shrink-0"
            >
              {quickAddLoading ? (
                <div className="size-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
            </Button>
          </form>
          {projects.length === 0 && (
            <p className="text-xs text-muted-foreground mt-1.5">
              Create a project first to add tasks.
            </p>
          )}
        </div>

      <TaskDialog
        open={taskDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setTaskDialogOpen(false);
            setEditingTask(null);
          } else {
            setTaskDialogOpen(true);
          }
        }}
        mode={editingTask ? "edit" : "create"}
        task={editingTask}
        onSuccess={handleTaskSuccess}
      />

      <TaskSearchDialog
        open={searchDialogOpen}
        onOpenChange={setSearchDialogOpen}
        tasks={baseFilteredTasks}
        onTaskClick={(task) => {
          setEditingTask(task);
          setTaskDialogOpen(true);
        }}
      />
    </div>
  );
}
